import { useSearchParams } from "react-router-dom";
import { Button } from "@satyrsmc/shared/components";

/** Range + Prev/Next pager; renders nothing when one page holds everything. */
export function Pager({
  page,
  perPage,
  total,
  onPageChange,
}: {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  if (total <= perPage) return null;
  const first = (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);
  const lastPage = Math.ceil(total / perPage);
  return (
    <div className="flex items-center gap-3 text-sm text-muted-foreground">
      <Button
        size="sm"
        variant="outline"
        disabled={page <= 1}
        aria-label="Previous page"
        onClick={() => onPageChange(page - 1)}
      >
        Previous
      </Button>
      <span>{`${first}–${last} of ${total}`}</span>
      <Button
        size="sm"
        variant="outline"
        disabled={page >= lastPage}
        aria-label="Next page"
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </Button>
    </div>
  );
}

/**
 * Page number sourced from the `?page` URL param so paginated views are
 * linkable and survive refresh. Page 1 keeps the URL clean (param removed).
 * Pass `replace` to correct the URL without adding a history entry.
 */
export function usePageParam(): [number, (next: number, options?: { replace?: boolean }) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = Number(searchParams.get("page"));
  const page = Number.isInteger(raw) && raw > 1 ? raw : 1;
  const setPage = (next: number, options?: { replace?: boolean }) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next <= 1) params.delete("page");
        else params.set("page", String(next));
        return params;
      },
      { replace: options?.replace },
    );
  };
  return [page, setPage];
}
